import {
  SolAcousticLayerTypes,
  SolBuildingTypeTypes,
  SolConformityTypes,
  SolInsulationTypes,
  SolNoiseLevelTypes,
} from "@iko-design-center/shared"
import { useHistory } from "react-router"
import { useRoutes } from "../routes/hooks"
import { Models } from "../../components/viewer/renderer.api"
import { useApplication } from "../application/hooks"
import { SolData } from "../sol/store"
import { useSol } from "../sol/hooks"
import { checkValidQueriedValueOfType, getEnumKey } from "./query-helpers"
import { useTranslation } from "react-i18next"

export function useQueryParams() {
  const { setCurrentModel } = useApplication()
  const { t } = useTranslation() as any
  const { Routes } = useRoutes()
  const history = useHistory()

  const {
    setSolInsulationType,
    setSolNoiseLevelType,
    setSolAcousticLayerType,
    setSolConformityType,
    setSolBuildingTypeType,
    setSolInsulationTypeWithThickness,
  } = useSol()

  const setQueryParams = (
    data: SolData,
    language: string,
    redirect = false
  ) => {
    const languageRoute = t("routes.SOL_4_OVERVIEW", { lng: language })
    const urlWithoutParams = `${window.location.origin}${languageRoute}`
    const url = new URL(urlWithoutParams ?? window.location.href)

    // Sol insulation
    const solInsulation = data.solInsulation.type
    const solInsulationEnum = getEnumKey(solInsulation, SolInsulationTypes)
    if (solInsulationEnum) {
      url.searchParams.append("insulation", solInsulationEnum)

      // Include insulation thickness
      const solInsulationThermalThickness =
        data.thermalPerformance.solInsulation.thickness
      if (solInsulationThermalThickness) {
        url.searchParams.append(
          "insulation-thickness",
          solInsulationThermalThickness.toString()
        )
      }
    }

    // Sol noise level
    const solNoiseLevel = data.solNoiseLevel.type
    const solNoiseLevelEnum = getEnumKey(solNoiseLevel, SolNoiseLevelTypes)
    if (solNoiseLevelEnum) {
      url.searchParams.append("noise-level", solNoiseLevelEnum)
    }

    // Sol acoustic layer
    const solAcousticLayer = data.solAcousticLayer.type
    const solAcousticLayerEnum = getEnumKey(
      solAcousticLayer,
      SolAcousticLayerTypes
    )
    if (solAcousticLayerEnum) {
      url.searchParams.append("acoustic-layer", solAcousticLayerEnum)
    }

    // Sol conformity
    const solConformity = data.solConformity.type
    const solConformityEnum = getEnumKey(solConformity, SolConformityTypes)
    if (solConformityEnum) {
      url.searchParams.append("conformity", solConformityEnum)
    }

    // Sol building type
    const solBuildingType = data.solBuildingType.type
    const solBuildingTypeEnum = getEnumKey(
      solBuildingType,
      SolBuildingTypeTypes
    )
    if (solBuildingTypeEnum) {
      url.searchParams.append("building-type", solBuildingTypeEnum)
    }

    if (redirect) {
      window.location.href = url.href
    } else {
      window.history.replaceState([], document.title, url.href as string)
    }
  }

  const redirectBasedOnQueryParams = () => {
    const url = new URL(window.location.href)
    const query = {
      solInsulation: url.searchParams.get("insulation"),
      solInsulationThickness: url.searchParams.get("insulation-thickness"),
      solNoiseLevel: url.searchParams.get("noise-level"),
      solAcousticLayer: url.searchParams.get("acoustic-layer"),
      solConformity: url.searchParams.get("conformity"),
      solBuildingType: url.searchParams.get("building-type"),
      detail: url.searchParams.get("detail"),
      token: url.searchParams.get("token"),
    }

    // Sol insulation
    const validSolInsulation = checkValidQueriedValueOfType(
      query.solInsulation,
      SolInsulationTypes
    )
    if (validSolInsulation) {
      // Include insulation thickness
      if (query.solInsulationThickness) {
        if (query.solInsulationThickness) {
          setSolInsulationTypeWithThickness(
            validSolInsulation,
            query.solInsulationThickness
          )
        } else {
          setSolInsulationType(validSolInsulation)
        }
      }
    }

    // Sol noise level
    const validSolNoiseLevel = checkValidQueriedValueOfType(
      query.solNoiseLevel,
      SolNoiseLevelTypes
    )
    if (validSolNoiseLevel) setSolNoiseLevelType(validSolNoiseLevel)

    // Sol acoustic layer
    const validSolAcousticLayer = checkValidQueriedValueOfType(
      query.solAcousticLayer,
      SolAcousticLayerTypes
    )
    if (validSolAcousticLayer) setSolAcousticLayerType(validSolAcousticLayer)

    // Sol conformity
    const validSolConformity = checkValidQueriedValueOfType(
      query.solConformity,
      SolConformityTypes
    )
    if (validSolConformity) setSolConformityType(validSolConformity)

    // Sol building type
    let validSolBuildingType = null
    if (validSolConformity === SolConformityTypes.P1_1_1) {
      validSolBuildingType = checkValidQueriedValueOfType(
        query.solBuildingType,
        SolBuildingTypeTypes
      )
      if (validSolBuildingType) setSolBuildingTypeType(validSolBuildingType)
    }

    // Model
    let validModel = null
    if (validSolInsulation && !validSolNoiseLevel && !validSolConformity) {
      validModel = Models.SOL_DALLEPORTEE
    }
    if (validSolInsulation && validSolNoiseLevel && !validSolConformity) {
      validModel = Models.SOL_SOUSCHAPE
    }
    if (validSolInsulation && !validSolNoiseLevel && validSolConformity) {
      validModel = Models.SOL_SOUSDALLAGE
    }

    if (validModel) setCurrentModel(validModel)
    else setCurrentModel(Models.SOL_SOUSDALLAGE)

    // Routing
    let route = Routes.SOL_1_INSULATION
    if (validSolInsulation) {
      if (validSolNoiseLevel && !validSolConformity) {
        if (validSolAcousticLayer) {
          route = Routes.SOL_4_OVERVIEW
        } else {
          route = Routes.SOL_3_ACOUSTIC_LAYER
        }
      } else if (!validSolNoiseLevel && validSolConformity) {
        if (
          (validSolConformity === SolConformityTypes.P1_1_1 &&
            validSolBuildingType !== null &&
            validSolBuildingType) ||
          validSolConformity === SolConformityTypes.P1_1_2
        ) {
          route = Routes.SOL_4_OVERVIEW
        } else {
          route = Routes.SOL_3_BUILDING_TYPE
        }
      } else {
        route = Routes.SOL_4_OVERVIEW
      }
    }

    if (query.token) {
      history.push({
        pathname: route as any,
        search: `?token=${query.token}`,
      })
    } else {
      history.push(route as any)
    }
  }

  return { setQueryParams, redirectBasedOnQueryParams }
}
